import React, { useState, useEffect } from 'react';
import { Database, TestTube, Save, AlertCircle, CheckCircle, Loader, Edit2, Trash2, Plus, List, Table } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../config/api';

const AdminDatabaseConfigPage = () => {
    const navigate = useNavigate();

    // Default ports for each database type
    const DEFAULT_PORTS = {
        postgresql: '5432',
        mysql: '3306',
        sqlserver: '1433',
        oracle: '1521'
    };

    const [config, setConfig] = useState({
        name: '',
        type: 'postgresql',
        host: '',
        port: '5432',
        database: '',
        username: '',
        password: '',
        trustServerCertificate: false,
        serverInstance: '' // For SQL Server named instances
    });

    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [saving, setSaving] = useState(false);
    const [configurations, setConfigurations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [view, setView] = useState('form'); // 'form' or 'list'

    const handleInputChange = (field, value) => {
        setConfig(prev => {
            const newConfig = { ...prev, [field]: value };

            // Auto-update port when database type changes
            if (field === 'type' && DEFAULT_PORTS[value]) {
                newConfig.port = DEFAULT_PORTS[value];
            }

            return newConfig;
        });

        // Clear test result when config changes
        if (testResult) {
            setTestResult(null);
        }
    };

    const testConnection = async () => {
        setTesting(true);
        setTestResult(null);

        try {
            let result;

            // If we're editing an existing configuration and no password is provided,
            // test using the stored configuration by ID
            if (editingId && (!config.password || config.password.trim() === '')) {
                result = await apiClient.post(`/admin/database/test-configuration/${editingId}`);
            } else {
                // Test using the form data (for new configs or when password is provided)
                result = await apiClient.post('/admin/database/test-connection', config);
            }

            setTestResult({ success: true, message: 'Connection successful!' });
        } catch (error) {
            setTestResult({ success: false, message: error.message || 'Connection failed' });
        } finally {
            setTesting(false);
        }
    };

    // Load configurations
    const loadConfigurations = async () => {
        setLoading(true);
        try {
            const result = await apiClient.get('/admin/database/configurations');
            setConfigurations(result.configurations || []);
        } catch (error) {
            console.error('Failed to load configurations:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadConfigurations();
    }, []);

    const saveConfiguration = async () => {
        setSaving(true);

        try {
            if (editingId) {
                await apiClient.put(`/admin/database/configurations/${editingId}`, config);
                alert('Configuration updated successfully!');
            } else {
                await apiClient.post('/admin/database/configurations', config);
                alert('Configuration saved successfully!');
            }
            resetForm();
            loadConfigurations();
            setView('list');
        } catch (error) {
            alert('Error saving configuration: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    // Helper functions
    const resetForm = () => {
        setConfig({
            name: '',
            type: 'postgresql',
            host: '',
            port: '5432',
            database: '',
            username: '',
            password: '',
            trustServerCertificate: false,
            serverInstance: ''
        });
        setEditingId(null);
        setTestResult(null);
    };

    const editConfiguration = (configuration) => {
        setConfig({
            name: configuration.name,
            type: configuration.type,
            host: configuration.host,
            port: configuration.port.toString(),
            database: configuration.database,
            username: configuration.username,
            password: '', // Don't populate password for security
            trustServerCertificate: configuration.trustServerCertificate || false,
            serverInstance: configuration.serverInstance || ''
        });
        setEditingId(configuration.id);
        setTestResult(null);
        setView('form');
    };

    const deleteConfiguration = async (id, name) => {
        if (!window.confirm(`Are you sure you want to delete the configuration "${name}"?`)) {
            return;
        }

        try {
            await apiClient.delete(`/admin/database/configurations/${id}`);
            alert('Configuration deleted successfully!');
            loadConfigurations();
        } catch (error) {
            alert('Error deleting configuration: ' + error.message);
        }
    };

    const startNewConfiguration = () => {
        resetForm();
        setView('form');
    };

    const isFormValid = config.name &&
        (config.type === 'sqlserver' ? config.serverInstance : config.host) &&
        config.database && config.username;

    return (
        <div>
            <div className="mb-6">
                <div className="flex justify-between items-start">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <Database size={24} />
                            Database Configuration
                        </h1>
                        <p className="text-gray-600">
                            Configure SQL database connections for data integration and field discovery.
                        </p>
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={() => setView('list')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-md ${
                                view === 'list'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            <List size={18} />
                            View All ({configurations.length})
                        </button>
                        <button
                            onClick={() => navigate('/admin/table-browser')}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            <Table size={18} />
                            Browse Tables
                        </button>
                        <button
                            onClick={startNewConfiguration}
                            className={`flex items-center gap-2 px-4 py-2 rounded-md ${
                                view === 'form' && !editingId
                                    ? 'bg-green-600 text-white'
                                    : 'bg-green-600 text-white hover:bg-green-700'
                            }`}
                        >
                            <Plus size={18} />
                            New Configuration
                        </button>
                    </div>
                </div>
            </div>

            {/* Configurations List View */}
            {view === 'list' && (
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="p-6 border-b">
                        <h2 className="text-lg font-semibold text-gray-900">Saved Database Configurations</h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Manage your database connections. Passwords are encrypted and stored securely.
                        </p>
                    </div>

                    {loading ? (
                        <div className="p-12 text-center">
                            <Loader className="animate-spin mx-auto mb-4 text-blue-600" size={32} />
                            <p className="text-gray-600">Loading configurations...</p>
                        </div>
                    ) : configurations.length === 0 ? (
                        <div className="p-12 text-center">
                            <Database className="mx-auto mb-4 text-gray-400" size={48} />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No configurations yet</h3>
                            <p className="text-gray-600 mb-4">
                                Create your first database configuration to get started with data integration.
                            </p>
                            <button
                                onClick={startNewConfiguration}
                                className="flex items-center gap-2 mx-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                            >
                                <Plus size={18} />
                                Create First Configuration
                            </button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Type
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Host
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Database
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Created
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {configurations.map((config) => (
                                        <tr key={config.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{config.name}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {config.type}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {config.host}:{config.port}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {config.database}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {new Date(config.created_at).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() => editConfiguration(config)}
                                                        className="text-blue-600 hover:text-blue-900 p-1"
                                                        title="Edit configuration"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => deleteConfiguration(config.id, config.name)}
                                                        className="text-red-600 hover:text-red-900 p-1"
                                                        title="Delete configuration"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Configuration Form */}
            {view === 'form' && (
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="p-6 border-b">
                        <h2 className="text-lg font-semibold text-gray-900">
                            {editingId ? 'Edit Configuration' : 'New Database Configuration'}
                        </h2>
                        {editingId && (
                            <p className="text-sm text-yellow-600 mt-1">
                                Leave password field empty to keep existing password
                            </p>
                        )}
                    </div>

            <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Configuration Name */}
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Configuration Name *
                        </label>
                        <input
                            type="text"
                            value={config.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="e.g., CRM Database, Customer Data"
                        />
                    </div>

                    {/* Database Type */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Database Type
                        </label>
                        <select
                            value={config.type}
                            onChange={(e) => handleInputChange('type', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="postgresql">PostgreSQL</option>
                            <option value="mysql">MySQL</option>
                            <option value="sqlserver">SQL Server</option>
                            <option value="oracle">Oracle</option>
                        </select>
                    </div>

                    {/* Server Instance - SQL Server Only */}
                    {config.type === 'sqlserver' ? (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Server Instance *
                            </label>
                            <input
                                type="text"
                                value={config.serverInstance}
                                onChange={(e) => handleInputChange('serverInstance', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="e.g., 192.168.1.100 or SERVER\INSTANCE"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Enter server name/IP and instance (if any). Examples: 192.168.1.100, SERVER\SQLEXPRESS
                            </p>
                        </div>
                    ) : (
                        /* Host - For non-SQL Server databases */
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Host *
                            </label>
                            <input
                                type="text"
                                value={config.host}
                                onChange={(e) => handleInputChange('host', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="localhost or IP address"
                            />
                        </div>
                    )}

                    {/* Port */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Port
                        </label>
                        <input
                            type="number"
                            value={config.port}
                            onChange={(e) => handleInputChange('port', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="5432"
                        />
                    </div>

                    {/* Database Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Database Name *
                        </label>
                        <input
                            type="text"
                            value={config.database}
                            onChange={(e) => handleInputChange('database', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="database_name"
                        />
                    </div>

                    {/* Username */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Username *
                        </label>
                        <input
                            type="text"
                            value={config.username}
                            onChange={(e) => handleInputChange('username', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="database_user"
                        />
                    </div>

                    {/* Password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Password
                        </label>
                        <input
                            type="password"
                            value={config.password}
                            onChange={(e) => handleInputChange('password', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="••••••••"
                        />
                    </div>

                    {/* Trust Server Certificate - SQL Server Only */}
                    {config.type === 'sqlserver' && (
                        <div className="md:col-span-2">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="trustServerCertificate"
                                    checked={config.trustServerCertificate}
                                    onChange={(e) => handleInputChange('trustServerCertificate', e.target.checked)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="trustServerCertificate" className="ml-2 block text-sm text-gray-700">
                                    Trust Server Certificate
                                </label>
                            </div>
                            <p className="text-xs text-gray-500 mt-1 ml-6">
                                Enable this option when connecting to SQL Server instances with self-signed certificates.
                                Leave unchecked for genuine/trusted certificates for better security.
                            </p>
                        </div>
                    )}
                </div>

                {/* Test Result */}
                {testResult && (
                    <div className={`mt-6 p-4 rounded-md flex items-center gap-2 ${
                        testResult.success
                            ? 'bg-green-50 border border-green-200 text-green-800'
                            : 'bg-red-50 border border-red-200 text-red-800'
                    }`}>
                        {testResult.success ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                        <span>{testResult.message}</span>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="mt-6 flex gap-4">
                    <button
                        onClick={testConnection}
                        disabled={!isFormValid || testing}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                        {testing ? <Loader className="animate-spin" size={20} /> : <TestTube size={20} />}
                        {testing ? 'Testing...' : 'Test Connection'}
                    </button>

                    <button
                        onClick={saveConfiguration}
                        disabled={!isFormValid || saving || (testResult && !testResult.success)}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                        {saving ? <Loader className="animate-spin" size={20} /> : <Save size={20} />}
                        {saving ? 'Saving...' : 'Save Configuration'}
                    </button>
                </div>

                {/* Info Box */}
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex">
                        <AlertCircle className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                        <div className="text-sm text-blue-700">
                            <p className="font-medium mb-1">Security Note:</p>
                            <p>Connection details are encrypted before storage. Test the connection before saving to ensure credentials are correct.</p>
                        </div>
                    </div>
                </div>
            </div>
                </div>
            )}
        </div>
    );
};

export default AdminDatabaseConfigPage;